import { FormControlLabel, Switch } from '@mui/material';
import { DateNavigation } from 'components/shared';
import MenuButton from 'components/shared/MenuButton';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import type { SingleOrRange } from 'components/shared/DateNavigation/DateNavigation.types';
import { useTranslation } from 'react-i18next';
import { toNativeDate } from 'utils/date';
import {
  DateNavigationContainer,
  PlanUnitSelectorContainer,
  RightContainer,
  SelectionLimit,
  StyledHeader,
  Top
} from './Header.styles';
import type { HeaderProps } from './Header.types';
import {
  HoursPayOutAction,
  MAX_REQUESTS_PER_ACTION
} from '../../HoursPayOutRequestsOverview.types';

const Header = ({
  selectedDate,
  selectedDateRange,
  period,
  onlyToApprove,
  onPeriodChange,
  onSelectedDate,
  onSelectedDateRange,
  onSelectPlanUnit,
  onOnlyToApproveChange,
  showActions = false,
  numberOfSelectedRequests = 0,
  onAction
}: HeaderProps) => {
  'use memo';

  const { t } = useTranslation('common');

  const tooManySelected = numberOfSelectedRequests > MAX_REQUESTS_PER_ACTION;

  const handleSelectedDateChange = (date: SingleOrRange) => {
    if (period === 'custom') {
      onSelectedDateRange(Array.isArray(date) ? date : undefined);
      return;
    }

    if (Array.isArray(date)) {
      onSelectedDate(toNativeDate(date[0]));
      return;
    }

    onSelectedDate(toNativeDate(date));
  };

  return (
    <StyledHeader as="header">
      <Top>
        <PlanUnitSelectorContainer>
          <PlanUnitSelector onSelected={onSelectPlanUnit} />
          <FormControlLabel
            control={
              <Switch
                checked={onlyToApprove}
                onChange={(_, checked) => onOnlyToApproveChange(checked)}
              />
            }
            label={t('hoursPayOutRequestsOverview.toBeApproved')}
          />
        </PlanUnitSelectorContainer>

        <DateNavigationContainer>
          <DateNavigation
            defaultPeriod="custom"
            options={['month', 'custom']}
            defaultDate={selectedDate}
            defaultDateRange={selectedDateRange}
            onDateChange={handleSelectedDateChange}
            onPeriodChange={onPeriodChange}
            allowMenuOptions
            persistPeriodPreference={false}
          />
        </DateNavigationContainer>

        <RightContainer>
          {showActions && tooManySelected && (
            <SelectionLimit>
              {t('hoursPayOutRequestsOverview.selectionTooLarge', {
                max: MAX_REQUESTS_PER_ACTION
              })}
            </SelectionLimit>
          )}
          {showActions && (
            <MenuButton
              label={t('actions')}
              disabled={numberOfSelectedRequests === 0 || tooManySelected}
              options={[
                {
                  label: t('hoursPayOutRequestsOverview.actions.PAY_OUT'),
                  onClick: () => onAction?.(HoursPayOutAction.PAY_OUT)
                },
                {
                  label: t('hoursPayOutRequestsOverview.actions.REJECT'),
                  onClick: () => onAction?.(HoursPayOutAction.REJECT)
                }
              ]}
            />
          )}
        </RightContainer>
      </Top>
    </StyledHeader>
  );
};

export default Header;
